import { AppFunctionComponent } from "../../../types"
import React, { ReactNode, useEffect, useRef, useState } from "react"
import { SecondaryHeading } from "../../../components/common/heading.component"
import { useSmoothScroll } from "../../../helpers/smooth-scroll"
import { useWindowEventWithoutRAF } from "../../../helpers/use-window-event.hook"
import Arrow from "../../../images/arrow.inline.svg"
import mainTheme from "../../../theme"
import { NeutralTextLink } from "../../../ui/components/link/link.component"
import {
  StaticContainer,
  StickyContainer,
  Wrapper,
  TitleWrapper,
  List,
  InlineContainer,
  SubmenuButton,
  ArrowButton,
} from "./sticky-submenu.styled"

interface Props {
  title: string
  items: { displayName: string; path: string }[]
  button?: ReactNode
  titleUrl: string
}

const StickySubmenu: AppFunctionComponent<Props> = ({
  className,
  title,
  items,
  button,
  titleUrl,
}) => {
  const [sticky, setStickyState] = useState(false)
  const [mobileMenuActive, setMobileMenuActiveState] = useState(false)
  const container = useRef<HTMLDivElement>(null)
  const {
    breakpoint: { tabletHorizontal },
    size: { headerHeightMobile, headerHeightDesktop },
  } = mainTheme
  let mainHeader: null | HTMLElement = null

  const onScroll = () => {
    if (!mainHeader) {
      mainHeader = document.getElementsByTagName("header").item(0)
    }

    const positionY = container.current!.getBoundingClientRect().top

    const breakpoint =
      window.innerWidth < tabletHorizontal
        ? headerHeightMobile
        : headerHeightDesktop

    // Push / pull header
    const headerTransform =
      positionY <= breakpoint ? -breakpoint + positionY : 0
    if (mainHeader) {
      mainHeader.style.transform = `translateY(${headerTransform}px)`
    }

    // Pin / unpin submenu
    setStickyState(positionY <= 0)
  }

  useEffect(() => {
    onScroll()
  }, [])

  const onArrowClick = () => {
    setMobileMenuActiveState(!mobileMenuActive)
  }

  useWindowEventWithoutRAF("scroll", onScroll)
  useSmoothScroll()

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    })
  }

  return (
    <StaticContainer className={className} ref={container}>
      <StickyContainer sticky={sticky}>
        <Wrapper aria-label={title}>
          {title && (
            <TitleWrapper to={titleUrl} onClick={scrollToTop}>
              <SecondaryHeading renderAs={"h3"}>{title}</SecondaryHeading>
            </TitleWrapper>
          )}
          <List active={mobileMenuActive}>
            {items.map(({ displayName, path }, index) => {
              const handleClickScroll = () => {
                if (path.includes("#")) {
                  document.getElementById(path)?.scrollIntoView()
                  setMobileMenuActiveState(false)
                }
              }
              return (
                <li key={index}>
                  <NeutralTextLink
                    onClick={handleClickScroll}
                    activeClassName={"active"}
                    to={path}
                  >
                    {displayName}
                  </NeutralTextLink>
                </li>
              )
            })}
          </List>
          {button && (
            <InlineContainer>
              <SubmenuButton active>{button}</SubmenuButton>
            </InlineContainer>
          )}
          <ArrowButton onClick={onArrowClick} active={mobileMenuActive}>
            <Arrow alt="Toggle submenu" />
          </ArrowButton>
        </Wrapper>
      </StickyContainer>
    </StaticContainer>
  )
}

export default StickySubmenu
